import type { OutageRow } from "./usePages.js";
import { formatKwh } from "./energy.js";

/**
 * Wording for the outage history. Every figure is the server's; this file
 * only says them (docs/ARCHITECTURE.md section 2).
 *
 * Times are on the site's clock, in en-GB as the energy labels are, so an
 * outage reads the same wherever the reader is.
 */

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string, withDate: boolean): Intl.DateTimeFormat {
  const id = `${timeZone}|${String(withDate)}`;
  let format = formatters.get(id);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      ...(withDate ? { day: "numeric", month: "short" } : {}),
    });
    formatters.set(id, format);
  }
  return format;
}

const days = new Map<string, Intl.DateTimeFormat>();

function dayOf(iso: string, timeZone: string): string {
  let format = days.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", { timeZone, dateStyle: "short" });
    days.set(timeZone, format);
  }
  return format.format(new Date(iso));
}

/** "14:05" on the site's clock, with the date when asked. */
export function siteTime(iso: string, timeZone: string, withDate = false): string {
  return formatter(timeZone, withDate).format(new Date(iso));
}

/** "24 Sept, 20:29 to 21:09", with the end's date only when it is another day. */
export function outageWhen(outage: OutageRow): string {
  const { start, end, timezone } = outage;
  const from = siteTime(start, timezone, true);
  if (end === null) return `${from}, still going`;
  const sameDay = dayOf(start, timezone) === dayOf(end, timezone);
  return `${from} to ${siteTime(end, timezone, !sameDay)}`;
}

/** "35 min", "1 h 50 min", "2 d 3 h". Under a minute reads as such. */
export function formatSpan(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return "under a minute";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const rest = minutes % 60;
  if (days > 0) return hours > 0 ? `${String(days)} d ${String(hours)} h` : `${String(days)} d`;
  if (hours > 0) return rest > 0 ? `${String(hours)} h ${String(rest)} min` : `${String(hours)} h`;
  return `${String(rest)} min`;
}

/**
 * Where the edges could really be. Readings come every few minutes, so the
 * grid went some time after the last reading that had it.
 */
export function edgeNote(outage: OutageRow): string {
  const { start, end, lastWithGrid, lastWithoutGrid, timezone } = outage;
  const went =
    lastWithGrid === null
      ? `The grid was already gone when records began, at ${siteTime(start, timezone)}.`
      : `The grid went between ${siteTime(lastWithGrid, timezone)} and ${siteTime(start, timezone)}.`;
  if (end === null) return went;
  return `${went} It was back between ${siteTime(lastWithoutGrid, timezone)} and ${siteTime(end, timezone)}.`;
}

/**
 * What the coverage figure leaves out, or null when nothing is. Each cause is
 * said plainly, because each is a stretch in which the house had no power or
 * nobody saw whether it had.
 */
export function coverageNote(outage: OutageRow): string | null {
  const notes: string[] = [];
  if (outage.outputOffSeconds > 0) {
    notes.push(
      `The inverter's output was off for ${formatSpan(outage.outputOffSeconds)} of it, though readings went on.`,
    );
  }
  const silent = outage.uncoveredSeconds - outage.outputOffSeconds;
  if (silent > 0) {
    notes.push(
      `The record stops ${formatSpan(silent)} before the grid came back. ` +
        "The inverter powers the dongle, so the battery most likely ran out there.",
    );
  }
  if (outage.unrecordedSeconds > 0) {
    notes.push(
      `${formatSpan(outage.unrecordedSeconds)} of it went unrecorded, so the share shown is the least it can have been.`,
    );
  }
  return notes.length === 0 ? null : notes.join(" ");
}

/** The lowest charge, said against the floor. */
export function chargeNote(outage: OutageRow): string {
  const lowest = `lowest ${String(outage.lowestSocPct)}%`;
  return outage.reachedReserve
    ? `${lowest}, at or under the ${String(outage.reserveSocPct)}% reserve`
    : lowest;
}

/** The battery's figure against the inverter's own counter, in words. */
export function counterNote(outage: OutageRow): string {
  const check = outage.dischargeCheck;
  if (check === null) {
    if (outage.ongoing) return "Checked once it is over.";
    return outage.unrecordedSeconds > 0 || outage.uncoveredSeconds > 0
      ? "Not checked: part of it went unseen."
      : "Not checked: the inverter's counter was missing.";
  }
  const counted = formatKwh(check.counterKwh);
  return check.agrees
    ? `Agrees: the inverter counted ${counted}.`
    : `Differs: the inverter counted ${counted}.`;
}
